import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Brand from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function AdminForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/request", { email });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="forgot-page">
      <header className="px-6 sm:px-12 pt-10 max-w-6xl w-full mx-auto">
        <Link to="/"><Brand size="sm" subtitle={true} /></Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80 mb-3">Password reset</p>
            <h1 className="font-display text-4xl">Forgot your password?</h1>
            <p className="text-sm text-muted-foreground mt-3">
              Enter your email. If the account exists, we&rsquo;ll send instructions.
            </p>
          </div>

          <div className="border border-border rounded-2xl bg-card p-7">
            {done ? (
              <div className="text-center space-y-4" data-testid="forgot-done">
                <p className="text-sm">
                  If an account exists for <span className="text-primary">{email}</span>, a reset
                  link has been issued.
                </p>
                <Link
                  to="/admin/login"
                  className="inline-block px-5 py-2 rounded-full border border-primary/40 text-primary hover:bg-primary/10"
                  data-testid="forgot-back-link"
                >
                  Back to sign in
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="forgot-submit-button"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
                </Button>
                <div className="text-center text-xs">
                  <Link to="/admin/login" className="text-muted-foreground hover:text-primary">
                    Back to sign in
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
